import React, { useState, useEffect, useCallback } from 'react';
import ApiClient from '../utils/ApiClient';
import { toIsoDate, minPickupDate } from './ShipmentPlanner';

function ShippingCoach() {
    const [pos, setPos] = useState([]);
    const [selectedPo, setSelectedPo] = useState('');
    const [packingLists, setPackingLists] = useState([]);
    const [selectedPl, setSelectedPl] = useState('');
    const [received, setReceived] = useState([]);
    const [rowData, setRowData] = useState({}); // keyed by asin: { dims, variants }
    // 3 days out. toIsoDate (LOCAL components) rather than toISOString(): the latter renders the
    // UTC day, which is tomorrow's date for anyone west of UTC during their evening -- the same
    // class of timezone slip that made run #255 send an already-expired instant to Amazon.
    const [readyDate, setReadyDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() + 3);
        return toIsoDate(d);
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [perRowState, setPerRowState] = useState({}); // { [asin]: { qty, variantId, result, running, error } }

    function updateRow(asin, patch) {
        setPerRowState(prev => ({...prev, [asin]: {...prev[asin], ...patch}}));
    }

    async function runCoach(row) {
        const s = perRowState[row.asin] || {};
        if (!s.variantId) { updateRow(row.asin, {error: 'Pick a variant first'}); return; }
        updateRow(row.asin, {running: true, error: null});
        try {
            const resp = await ApiClient.post('/api/coach/analyze', {
                asin: row.asin, sku: row.sku, qty: s.qty || row.receivedQty,
                variantId: s.variantId, readyToShipDate: readyDate,
            });
            updateRow(row.asin, {running: false, result: resp.data});
        } catch (e) {
            updateRow(row.asin, {running: false, error: String(e.response?.data || e)});
        }
    }

    async function createPlan(row) {
        const s = perRowState[row.asin] || {};
        if (!s.variantId) { updateRow(row.asin, {error: 'Pick a variant first'}); return; }
        if (!window.confirm(`Create inbound plan for ${s.qty || row.receivedQty} units of ${row.asin}? This is non-reversible from this page.`)) return;
        updateRow(row.asin, {creating: true, error: null});
        try {
            const resp = await ApiClient.post('/api/coach/create-plan', {
                asin: row.asin, sku: row.sku, qty: s.qty || row.receivedQty,
                variantId: s.variantId, readyToShipDate: readyDate,
            });
            if (resp.data.status === 'CONFIRMED') {
                updateRow(row.asin, {creating: false, planId: resp.data.inboundPlanId});
            } else {
                updateRow(row.asin, {creating: false, error: `${resp.data.errorCode}: ${resp.data.errorMessage}`});
            }
        } catch (e) {
            const data = e.response?.data;
            const msg = (data && data.errorMessage) ? `${data.errorCode}: ${data.errorMessage}` : String(e);
            updateRow(row.asin, {creating: false, error: msg});
        }
    }

    useEffect(() => {
        ApiClient.get('/api/coach/po-list').then(r => setPos(r.data || [])).catch(e => setError(String(e)));
    }, []);

    useEffect(() => {
        if (!selectedPo) { setPackingLists([]); return; }
        ApiClient.get(`/api/coach/packing-list/${encodeURIComponent(selectedPo)}`)
            .then(r => { setPackingLists(r.data || []); setSelectedPl(''); })
            .catch(e => setError(String(e)));
    }, [selectedPo]);

    const onVariantSaved = useCallback((asin, v) => {
        setRowData(prev => ({...prev, [asin]: {...prev[asin], variants: [v, ...(prev[asin]?.variants || [])]}}));
    }, []);

    const onDimsUpdated = useCallback((asin, dims) => {
        setRowData(prev => ({...prev, [asin]: {...prev[asin], dims}}));
    }, []);

    const loadReceivedAndDetails = useCallback(async () => {
        if (!selectedPo || !selectedPl) { setReceived([]); return; }
        setLoading(true);
        try {
            const rec = await ApiClient.get(
                `/api/coach/received/${encodeURIComponent(selectedPo)}/${encodeURIComponent(selectedPl)}`);
            setReceived(rec.data || []);
            // Lazy-load per-row dims + variants in parallel
            const detail = {};
            await Promise.all((rec.data || []).map(async (row) => {
                const [dims, variants] = await Promise.all([
                    ApiClient.get(`/api/coach/product-dims/${row.asin}`).then(r => r.data).catch(() => null),
                    ApiClient.get(`/api/coach/variants/${row.asin}`).then(r => r.data).catch(() => []),
                ]);
                detail[row.asin] = { dims, variants };
            }));
            setRowData(detail);
        } catch (e) {
            setError(String(e));
        } finally {
            setLoading(false);
        }
    }, [selectedPo, selectedPl]);

    useEffect(() => { loadReceivedAndDetails(); }, [loadReceivedAndDetails]);

    return (
        <div style={{padding: '1rem'}}>
            <h1>Shipping Coach</h1>
            {error && <div style={{color: 'red'}}>{error}</div>}
            <div style={{display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem'}}>
                <label>PO:&nbsp;
                    <select value={selectedPo} onChange={e => setSelectedPo(e.target.value)}>
                        <option value="">(select)</option>
                        {pos.map(p => (
                            <option key={p.poNumber} value={p.poNumber}>
                                {p.poNumber} ({p.packingListCount} packing list{p.packingListCount === 1 ? '' : 's'})
                            </option>
                        ))}
                    </select>
                </label>
                <label>Packing list:&nbsp;
                    <select value={selectedPl} onChange={e => setSelectedPl(e.target.value)} disabled={!selectedPo}>
                        <option value="">(select)</option>
                        {packingLists.map(pl => (
                            <option key={pl.id} value={pl.packingListName}>{pl.packingListName}</option>
                        ))}
                    </select>
                </label>
                {/* min: this input had no floor at all, so it would happily offer a date already
                    gone -- which Amazon rejects outright ("DateTime value ... cannot be in the
                    past"), taking every transportation option with it. Shares the planner's floor
                    so both pickers agree on what is selectable. */}
                <label>Ready-to-ship date:&nbsp;
                    <input type="date" value={readyDate} min={minPickupDate()}
                           onChange={e => setReadyDate(e.target.value)} />
                </label>
            </div>

            {loading && <div>Loading…</div>}
            {!loading && received.map(row => {
                const s = perRowState[row.asin] || {};
                return (
                    <div key={row.asin} style={{border: '1px solid #ccc', borderRadius: 6, padding: '0.75rem', marginBottom: '0.75rem'}}>
                        <div><b>ASIN:</b> {row.asin} &nbsp;<b>SKU:</b> {row.sku} &nbsp;<b>Received:</b> {row.receivedQty}</div>
                        <div style={{color: '#666', fontSize: '0.9em'}}>{row.productName}</div>
                        <ProductDimsPanel asin={row.asin} dims={rowData[row.asin]?.dims} onUpdated={onDimsUpdated} />
                        <VariantsPanel asin={row.asin} sku={row.sku}
                                       variants={rowData[row.asin]?.variants || []}
                                       allowedModes={rowData[row.asin]?.dims?.allowedModes || 'BOTH'}
                                       onVariantSaved={onVariantSaved}
                                       selectedVariantId={s.variantId}
                                       onVariantChange={vid => updateRow(row.asin, {variantId: vid})} />
                        <CoachRow row={row} state={s}
                                  onRun={() => runCoach(row)}
                                  onCreate={() => createPlan(row)}
                                  onQtyChange={q => updateRow(row.asin, {qty: q})} />
                    </div>
                );
            })}
        </div>
    );
}

function ProductDimsPanel({asin, dims, onUpdated}) {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState({L: 0, W: 0, H: 0, weight: 0});

    useEffect(() => {
        if (dims) setDraft({L: dims.unitLengthIn ?? 0, W: dims.unitWidthIn ?? 0,
                            H: dims.unitHeightIn ?? 0, weight: dims.unitWeightLbs ?? 0});
    }, [dims]);

    if (!dims) return <div style={{color: '#999'}}>Loading dims…</div>;

    async function saveDims() {
        await ApiClient.put(`/api/coach/product-dims/${asin}`, {
            unitLengthIn: draft.L, unitWidthIn: draft.W, unitHeightIn: draft.H, unitWeightLbs: draft.weight,
        });
        setEditing(false);
        const fresh = await ApiClient.get(`/api/coach/product-dims/${asin}`);
        onUpdated(asin, fresh.data);
    }

    async function changeAllowedModes(newVal) {
        await ApiClient.put(`/api/coach/allowed-modes/${asin}`, {allowedModes: newVal});
        const fresh = await ApiClient.get(`/api/coach/product-dims/${asin}`);
        onUpdated(asin, fresh.data);
    }

    return (
        <div style={{background: '#f6f6f6', padding: '0.5rem', marginTop: '0.5rem'}}>
            <b>Product (per unit):</b>{' '}
            {editing ? (
                <>
                    <input type="number" value={draft.L} onChange={e => setDraft({...draft, L: parseFloat(e.target.value)})} style={{width: 60}}/> ×{' '}
                    <input type="number" value={draft.W} onChange={e => setDraft({...draft, W: parseFloat(e.target.value)})} style={{width: 60}}/> ×{' '}
                    <input type="number" value={draft.H} onChange={e => setDraft({...draft, H: parseFloat(e.target.value)})} style={{width: 60}}/> in /{' '}
                    <input type="number" value={draft.weight} onChange={e => setDraft({...draft, weight: parseFloat(e.target.value)})} style={{width: 60}}/> lb
                    &nbsp;<button onClick={saveDims}>Save</button>
                    &nbsp;<button onClick={() => setEditing(false)}>Cancel</button>
                </>
            ) : (
                <>
                    {dims.unitLengthIn != null
                        ? `${dims.unitLengthIn} × ${dims.unitWidthIn} × ${dims.unitHeightIn} in · ${dims.unitWeightLbs} lb · source: ${dims.unitDimsSource}`
                        : 'dims not yet captured'}
                    &nbsp;<button onClick={() => setEditing(true)}>Edit</button>
                </>
            )}
            <span style={{marginLeft: '1rem'}}>
                Modes allowed:{' '}
                <select value={dims.allowedModes || 'BOTH'} onChange={e => changeAllowedModes(e.target.value)}>
                    <option value="BOTH">BOTH</option>
                    <option value="UPS_ONLY">UPS_ONLY</option>
                    <option value="LTL_ONLY">LTL_ONLY</option>
                </select>
            </span>
        </div>
    );
}

function NewVariantModal({asin, sku, allowedModes, onClose, onSaved}) {
    const modeOptions = allowedModes === 'UPS_ONLY' ? ['UPS']
                       : allowedModes === 'LTL_ONLY' ? ['LTL']
                       : ['UPS', 'LTL'];
    const [mode, setMode] = useState(modeOptions[0]);
    const [unitsPerBox, setUnitsPerBox] = useState(1);
    const [box, setBox] = useState({L: 0, W: 0, H: 0, weight: 0});
    const [pallet, setPallet] = useState({perPallet: 0, L: 48, W: 40, H: 46, tare: 50, stackability: 'NON_STACKABLE'});
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);

    async function submit() {
        setSaving(true); setErr(null);
        try {
            const body = {
                asin, sku, mode, unitsPerBox,
                boxLengthIn: box.L, boxWidthIn: box.W, boxHeightIn: box.H, boxWeightLbs: box.weight,
                boxesPerPallet: mode === 'LTL' ? pallet.perPallet : null,
                palletLengthIn: mode === 'LTL' ? pallet.L : null,
                palletWidthIn:  mode === 'LTL' ? pallet.W : null,
                palletHeightIn: mode === 'LTL' ? pallet.H : null,
                palletTareLbs:  mode === 'LTL' ? pallet.tare : null,
                stackability:   mode === 'LTL' ? pallet.stackability : null,
            };
            const resp = await ApiClient.post('/api/coach/variants', body);
            onSaved(resp.data);
        } catch (e) {
            setErr(String(e.response?.data || e));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div style={{position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
                     alignItems: 'center', justifyContent: 'center', zIndex: 100}}>
            <div style={{background: 'white', padding: '1.5rem', borderRadius: 8, minWidth: 400}}>
                <h2>New variant for {asin}</h2>
                <div>
                    Mode:{' '}
                    {modeOptions.map(m => (
                        <label key={m} style={{marginRight: '0.5rem'}}>
                            <input type="radio" name="mode" value={m} checked={mode === m} onChange={() => setMode(m)} /> {m}
                        </label>
                    ))}
                </div>
                <div style={{marginTop: '0.5rem'}}>
                    Units per box: <input type="number" value={unitsPerBox} onChange={e => setUnitsPerBox(parseInt(e.target.value, 10) || 0)} style={{width: 60}}/>
                </div>
                <div style={{marginTop: '0.5rem'}}>
                    Box (in × in × in / lb):{' '}
                    <input type="number" placeholder="L" value={box.L} onChange={e => setBox({...box, L: parseFloat(e.target.value)})} style={{width: 60}}/> ×{' '}
                    <input type="number" placeholder="W" value={box.W} onChange={e => setBox({...box, W: parseFloat(e.target.value)})} style={{width: 60}}/> ×{' '}
                    <input type="number" placeholder="H" value={box.H} onChange={e => setBox({...box, H: parseFloat(e.target.value)})} style={{width: 60}}/> /{' '}
                    <input type="number" placeholder="lb" value={box.weight} onChange={e => setBox({...box, weight: parseFloat(e.target.value)})} style={{width: 60}}/>
                </div>
                {mode === 'LTL' && (
                    <div style={{marginTop: '0.5rem'}}>
                        <div>Boxes per pallet: <input type="number" value={pallet.perPallet}
                            onChange={e => setPallet({...pallet, perPallet: parseInt(e.target.value, 10) || 0})} style={{width: 60}}/></div>
                        <div>Pallet (in × in × in / tare lb):{' '}
                            <input type="number" value={pallet.L} onChange={e => setPallet({...pallet, L: parseFloat(e.target.value)})} style={{width: 60}}/> ×{' '}
                            <input type="number" value={pallet.W} onChange={e => setPallet({...pallet, W: parseFloat(e.target.value)})} style={{width: 60}}/> ×{' '}
                            <input type="number" value={pallet.H} onChange={e => setPallet({...pallet, H: parseFloat(e.target.value)})} style={{width: 60}}/> /{' '}
                            <input type="number" value={pallet.tare} onChange={e => setPallet({...pallet, tare: parseFloat(e.target.value)})} style={{width: 60}}/>
                        </div>
                        <div>Stackability:{' '}
                            <select value={pallet.stackability} onChange={e => setPallet({...pallet, stackability: e.target.value})}>
                                <option value="STACKABLE">STACKABLE</option>
                                <option value="NON_STACKABLE">NON_STACKABLE</option>
                            </select>
                        </div>
                    </div>
                )}
                {err && <div style={{color: 'red', marginTop: '0.5rem'}}>{err}</div>}
                <div style={{marginTop: '1rem', display: 'flex', justifyContent: 'flex-end', gap: '0.5rem'}}>
                    <button onClick={onClose} disabled={saving}>Cancel</button>
                    <button onClick={submit} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
                </div>
            </div>
        </div>
    );
}

function VariantsPanel({asin, sku, variants, allowedModes, onVariantSaved, selectedVariantId, onVariantChange}) {
    const [showModal, setShowModal] = useState(false);
    return (
        <div style={{marginTop: '0.5rem'}}>
            <b>Variant:</b>{' '}
            <select value={selectedVariantId || ''} onChange={e => onVariantChange(e.target.value ? parseInt(e.target.value, 10) : null)}>
                <option value="">(select)</option>
                {variants.map(v => (
                    <option key={v.id} value={v.id}>{v.autoName} — used {v.useCount}×{v.lastUsedAt ? `, last ${v.lastUsedAt.slice(0,10)}` : ''}</option>
                ))}
            </select>
            &nbsp;<button onClick={() => setShowModal(true)}>+ New variant</button>
            {showModal && (
                <NewVariantModal
                    asin={asin}
                    sku={sku}
                    allowedModes={allowedModes}
                    onClose={() => setShowModal(false)}
                    onSaved={(v) => { setShowModal(false); onVariantSaved(asin, v); onVariantChange(v.id); }}
                />
            )}
        </div>
    );
}

function CoachRow({row, state, onRun, onCreate, onQtyChange}) {
    const planId = state.planId;
    return (
        <div style={{marginTop: '0.5rem'}}>
            <label>Qty to ship:&nbsp;
                <input type="number" value={state.qty ?? row.receivedQty}
                       onChange={e => onQtyChange(parseInt(e.target.value, 10))}
                       style={{width: 80}}/>
            </label>
            &nbsp;
            <button onClick={onRun} disabled={!state.variantId || state.running || state.creating}>
                {state.running ? 'Running…' : 'Run coach'}
            </button>
            &nbsp;
            <button onClick={onCreate} disabled={!state.variantId || state.creating}>
                {state.creating ? 'Creating…' : 'Create plan'}
            </button>
            {state.error && <span style={{color: 'red'}}>&nbsp;{state.error}</span>}
            {state.result && !planId && (
                <div style={{background: '#eef', padding: '0.5rem', marginTop: '0.5rem'}}>
                    <b>{state.result.shippingMode}</b> · <b>{state.result.carrierName || '(no carrier)'}</b> ·{' '}
                    <b>${state.result.totalCost != null ? state.result.totalCost.toFixed(2) : '—'}</b>
                    {state.result.pickupDate && <> · pickup {state.result.pickupDate}</>}
                    {(state.result.fcs || []).length > 0 && (
                        <ul style={{margin: '0.25rem 0 0 1.25rem'}}>
                            {state.result.fcs.map((f, i) => (
                                <li key={i}>{f.fcCode} ({f.city}, {f.state}): {f.units} units · ${f.cost != null ? f.cost.toFixed(2) : '—'}</li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
            {planId && (
                <div style={{background: '#efe', padding: '0.5rem', marginTop: '0.5rem'}}>
                    Plan confirmed: <a href={`/shipment-queue?planId=${planId}`}>{planId}</a>
                </div>
            )}
        </div>
    );
}

export default ShippingCoach;
